import React, { useState, useEffect, useId } from "react"
import "../../../../styleSheets/base/styles.scss"
import "../styles/org.styles.scss"
import {
  Avatar, Card, Button, IconButton, Menu, MenuItem, Stack,
  Box, Typography, ListItemIcon, ListItemText,
} from "@mui/material"
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep"
import EditNoteIcon from "@mui/icons-material/EditNote"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined"
import CallOutlinedIcon from "@mui/icons-material/CallOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined"
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded"
import Helpers from "../../../../base/utils/Helpers"
import api from "../../../../base/services/ResApi"
import { Buffer } from "buffer"

const ApproveCardItem = (props) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const cardId = useId()
  const { orgCode, setOrgLogo } = props
  const name = props.orgName ? Helpers.underscoreRoleFormat(props.orgName) : "Unnamed organization"
  const summary = props.grpTmnlSummary

  useEffect(() => {
    let current = true
    api.get("/organizations/logo/" + orgCode, {
      headers: { "Is-Pending": "false" }, responseType: "arraybuffer",
    }).then((res) => {
      if (current) setOrgLogo(Buffer.from(res.data, "binary").toString("base64"))
    }).catch(() => {
      if (current) setOrgLogo(undefined)
    })
    return () => { current = false }
  }, [orgCode, setOrgLogo])

  const handleClose = () => setAnchorEl(null)
  const address = [props.primAddress, props.secAddress].filter(Boolean).join(" ")
  const contacts = [props.primContact, props.secContact].filter(Boolean).join(" / ")

  return (
    <Card component="article" aria-labelledby={`${cardId}-name`} variant="outlined" sx={{
      height: "100%", minWidth: 0, display: "flex", flexDirection: "column",
      borderRadius: 3, borderColor: "#dbe5e5", bgcolor: "#fff",
      boxShadow: "0 3px 12px rgba(21, 65, 67, 0.06)",
      transition: "border-color 150ms, box-shadow 150ms",
      "&:hover": { borderColor: "#9bbfbe", boxShadow: "0 5px 18px rgba(21, 65, 67, 0.10)" },
    }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: 2.5, bgcolor: "#116c6f", color: "white" }}>
        <Avatar variant="rounded" src={props.orgLogo ? "data:image/png;base64," + props.orgLogo : undefined} alt={name}
          sx={{ width: 48, height: 48, bgcolor: "#e1f0ef", color: "#116c6f", fontWeight: 700, borderRadius: 2, "& img": { objectFit: "contain", bgcolor: "white" } }}>
          {name.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id={`${cardId}-name`} component="h3" sx={{ fontSize: 21, fontWeight: 600, lineHeight: 1.4, overflowWrap: "anywhere" }}>{name}</Typography>
          <Typography variant="caption" sx={{ display: "block", fontSize: 14, mt: 0.75, color: "#d7eeee", overflowWrap: "anywhere" }}>
            Organization code <Box component="span" sx={{ ml: 0.75, color: "white", fontWeight: 600, letterSpacing: 0.5 }}>{orgCode || "Not available"}</Box>
          </Typography>
        </Box>
        <IconButton id={`${cardId}-menu-button`} size="small" aria-label={`Actions for ${name}`} aria-haspopup="menu"
          aria-controls={anchorEl ? `${cardId}-menu` : undefined} aria-expanded={anchorEl ? "true" : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)} sx={{ color: "white", mt: -0.5 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu id={`${cardId}-menu`} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} MenuListProps={{ "aria-labelledby": `${cardId}-menu-button` }}>
          <MenuItem onClick={() => { props.setEditModalOpen(true); handleClose() }}>
            <ListItemIcon><EditNoteIcon /></ListItemIcon><ListItemText>Edit organization</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { props.setDeleteModalopen(true); handleClose() }} sx={{ color: "error.main" }}>
            <ListItemIcon><DeleteSweepIcon color="error" /></ListItemIcon><ListItemText>Delete organization</ListItemText>
          </MenuItem>
        </Menu>
      </Stack>

      <Stack spacing={2.25} sx={{ p: 2.5, flex: 1 }}>
        <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
          {[["Total groups", summary?.totGroups, AccountTreeOutlinedIcon], ["Total terminals", summary?.totTerminals, PointOfSaleOutlinedIcon]].map(([label, value, Icon]) =>
            <Box key={label} sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f0f7f6", border: "1px solid #e0edeb" }}>
              <Stack component="dt" direction="row" spacing={0.75} alignItems="center" sx={{ color: "#466b6c" }}>
                <Icon sx={{ fontSize: 17 }} /><Typography component="span" variant="caption" sx={{ fontSize: 14 }}>{label}</Typography>
              </Stack>
              <Typography component="dd" sx={{ m: 0, mt: 0.5, fontSize: 32, lineHeight: 1.2, fontWeight: 700, color: "#164f51", fontVariantNumeric: "tabular-nums" }}>{value ?? "�"}</Typography>
            </Box>
          )}
        </Box>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {[["Address", address, PinDropOutlinedIcon], ["Phone", contacts, CallOutlinedIcon], ["Email", props.email, EmailOutlinedIcon]].map(([label, value, Icon]) =>
            <Stack key={label} direction="row" spacing={1.25} alignItems="flex-start">
              <Icon sx={{ fontSize: 19, color: "#759091", mt: 0.25 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 14, lineHeight: 1.4 }}>{label}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontSize: 16, lineHeight: 1.5, color: value ? "#354b54" : "text.secondary", overflowWrap: "anywhere" }}>{value || "Not provided"}</Typography>
              </Box>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ px: 2.5, py: 1.75, borderTop: "1px solid #e4ecec", bgcolor: "#fafcfc", "& .MuiButton-root": { textTransform: "none", borderRadius: 2, fontSize: 14 } }}>
        <Button variant="contained" disableElevation endIcon={<ArrowForwardRoundedIcon />} onClick={() => props.handleNavigate(orgCode)} sx={{ bgcolor: "#116c6f", "&:hover": { bgcolor: "#0c5659" } }}>View groups</Button>
        <Button startIcon={<CloudUploadOutlinedIcon />} onClick={() => props.setUploadModalOpen(true)} sx={{ color: "#116c6f" }}>Upload APK</Button>
        <Button  startIcon={<InfoOutlinedIcon />} onClick={() => props.setMoreInfoModalOpen(true)} sx={{ color: "#526b74", ml: "auto !important" }}>Details</Button>
      </Stack>
    </Card>
  )
}

export default ApproveCardItem
